import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Chip,
  Collapse,
  IconButton
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Search as SearchIcon
} from '@material-ui/icons';
import ConsultaPlazasService from '../../../services/ConsultaPlazasService';

const useStyles = makeStyles((theme) => ({
  filterCard: {
    marginBottom: theme.spacing(2),
    backgroundColor: '#f8f9fa',
  },
  filterHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: theme.spacing(1),
  },
  filterContent: {
    padding: theme.spacing(2),
  },
  formControl: {
    minWidth: 200,
  },
  filterActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  activeFiltersContainer: {
    marginTop: theme.spacing(2),
  },
  filterChip: {
    margin: theme.spacing(0.5),
  },
}));

const FiltrosSimples = ({
  selectedCarrera,
  selectedUniversidad,
  onCarreraChange,
  onUniversidadChange,
  onFiltrosChange,
  filtrosActivos = {}
}) => {
  const classes = useStyles();
  
  const [expanded, setExpanded] = useState(false);
  const [carreras, setCarreras] = useState([]);
  const [universidades, setUniversidades] = useState([]);
  
  // Estados de filtros simples - inicializar con filtros activos
  const [region, setRegion] = useState(filtrosActivos.region || '');
  const [nivelServicio, setNivelServicio] = useState(filtrosActivos.nivelServicio || '');
  const [estadoConfirmacion, setEstadoConfirmacion] = useState(filtrosActivos.estadoConfirmacion || '');

  // Estado para detectar cambios pendientes
  const [haycambiosPendientes, setHayChangiosPendientes] = useState(false);

  const regiones = [
    'Capital', 'Central', 'Alto Paraná', 'Itapúa', 'Caaguazú',
    'Caazapá', 'Canindeyú', 'Concepción', 'Cordillera', 'Guairá',
    'Misiones', 'Ñeembucú', 'Paraguarí', 'Presidente Hayes',
    'San Pedro', 'Amambay', 'Boquerón'
  ];

  const niveles = ['Nivel I', 'Nivel II', 'Nivel III', 'Nivel IV'];

  useEffect(() => {
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    try {
      const [carrerasData, universidadesData] = await Promise.all([
        ConsultaPlazasService.getCarreras().catch(() => [
          { id: 1, descripcion: 'Medicina' },
          { id: 2, descripcion: 'Enfermería' },
          { id: 3, descripcion: 'Odontología' }
        ]),
        ConsultaPlazasService.getUniversidades().catch(() => [
          { id: 1, institucion: 'Universidad Nacional de Asunción' },
          { id: 2, institucion: 'Universidad Católica' },
          { id: 3, institucion: 'Universidad del Norte' }
        ])
      ]);
      
      setCarreras(carrerasData);
      setUniversidades(universidadesData);
    } catch (error) {
      console.error('Error al cargar opciones de filtros:', error);
    }
  };

  const handleRegionChange = (event) => {
    const value = event.target.value;
    setRegion(value);
    setHayChangiosPendientes(true);
    console.log('Región seleccionada:', value);
  };

  const handleNivelChange = (event) => {
    const value = event.target.value;
    setNivelServicio(value);
    setHayChangiosPendientes(true);
    console.log('Nivel seleccionado:', value);
  };

  const handleEstadoChange = (event) => {
    const value = event.target.value;
    setEstadoConfirmacion(value);
    setHayChangiosPendientes(true);
    console.log('Estado seleccionado:', value);
  };

  const aplicarFiltros = () => {
    const filtros = {
      region,
      nivelServicio,
      estadoConfirmacion,
      carrera: selectedCarrera,
      universidad: selectedUniversidad
    };

    setHayChangiosPendientes(false);

    if (onFiltrosChange) {
      onFiltrosChange(filtros);
    }
  };

  const clearAllFilters = () => {
    setRegion('');
    setNivelServicio('');
    setEstadoConfirmacion('');
    onCarreraChange('');
    onUniversidadChange('');

    // Aplicar filtros vacíos inmediatamente
    if (onFiltrosChange) {
      onFiltrosChange({});
    }

    console.log('Todos los filtros limpiados y aplicados');
  };

  const getActiveFilters = () => {
    const active = [];
    
    if (selectedCarrera) {
      const carrera = carreras.find(c => c.id == selectedCarrera);
      active.push({
        key: 'carrera',
        label: `Carrera: ${carrera?.descripcion || selectedCarrera}`,
      });
    }
    
    if (selectedUniversidad) {
      const universidad = universidades.find(u => u.id == selectedUniversidad);
      active.push({
        key: 'universidad',
        label: `Universidad: ${universidad?.institucion || selectedUniversidad}`,
      });
    }

    if (region) active.push({ key: 'region', label: `Región: ${region}` });
    if (nivelServicio) active.push({ key: 'nivelServicio', label: `Nivel: ${nivelServicio}` });
    if (estadoConfirmacion) active.push({ key: 'estadoConfirmacion', label: `Estado: ${estadoConfirmacion}` });

    return active;
  };

  const clearFilter = (filterKey) => {
    switch (filterKey) {
      case 'carrera':
        onCarreraChange('');
        break;
      case 'universidad':
        onUniversidadChange('');
        break;
      case 'region':
        setRegion('');
        break;
      case 'nivelServicio':
        setNivelServicio('');
        break;
      case 'estadoConfirmacion':
        setEstadoConfirmacion('');
        break;
    }

    // Aplicar filtros después de limpiar uno específico
    setTimeout(() => {
      aplicarFiltros();
    }, 100);

    console.log('Filtro eliminado:', filterKey);
  };

  const activeFilters = getActiveFilters();

  return (
    <Card className={classes.filterCard}>
      <div className={classes.filterHeader} onClick={() => setExpanded(!expanded)}>
        <Box display="flex" alignItems="center">
          <FilterIcon style={{ marginRight: 8 }} />
          <Typography variant="h6">
            Filtros
          </Typography>
          {activeFilters.length > 0 && (
            <Chip
              label={`${activeFilters.length} activos`}
              size="small"
              color="primary"
              style={{ marginLeft: 8 }}
            />
          )}
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>

      <Collapse in={expanded}>
        <CardContent className={classes.filterContent}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth className={classes.formControl}>
                <InputLabel>Carrera</InputLabel>
                <Select
                  value={selectedCarrera}
                  onChange={(e) => {
                    onCarreraChange(e.target.value);
                    setHayChangiosPendientes(true);
                  }}
                >
                  <MenuItem value="">Todas las carreras</MenuItem>
                  {carreras.map((carrera) => (
                    <MenuItem key={carrera.id} value={carrera.id}>
                      {carrera.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth className={classes.formControl}>
                <InputLabel>Universidad</InputLabel>
                <Select
                  value={selectedUniversidad}
                  onChange={(e) => {
                    onUniversidadChange(e.target.value);
                    setHayChangiosPendientes(true);
                  }}
                >
                  <MenuItem value="">Todas las universidades</MenuItem>
                  {universidades.map((universidad) => (
                    <MenuItem key={universidad.id} value={universidad.id}>
                      {universidad.institucion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth className={classes.formControl}>
                <InputLabel>Región</InputLabel>
                <Select
                  value={region}
                  onChange={handleRegionChange}
                >
                  <MenuItem value="">Todas las regiones</MenuItem>
                  {regiones.map((reg) => (
                    <MenuItem key={reg} value={reg}>
                      {reg}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth className={classes.formControl}>
                <InputLabel>Nivel</InputLabel>
                <Select
                  value={nivelServicio}
                  onChange={handleNivelChange}
                >
                  <MenuItem value="">Todos los niveles</MenuItem>
                  {niveles.map((nivel) => (
                    <MenuItem key={nivel} value={nivel}>
                      {nivel}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth className={classes.formControl}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={estadoConfirmacion}
                  onChange={handleEstadoChange}
                >
                  <MenuItem value="">Todos los estados</MenuItem>
                  <MenuItem value="confirmado">Confirmado</MenuItem>
                  <MenuItem value="pendiente">Pendiente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <div className={classes.filterActions}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={aplicarFiltros}
              style={{
                marginRight: 8,
                backgroundColor: haycambiosPendientes ? '#ff9800' : undefined
              }}
            >
              {haycambiosPendientes ? 'Aplicar Cambios' : 'Aplicar Filtros'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearAllFilters}
              disabled={activeFilters.length === 0}
            >
              Limpiar Todo
            </Button>
          </div>

          {activeFilters.length > 0 && (
            <div className={classes.activeFiltersContainer}>
              <Typography variant="body2" gutterBottom>
                Filtros activos:
              </Typography>
              <Box>
                {activeFilters.map((filter) => (
                  <Chip
                    key={filter.key}
                    label={filter.label}
                    onDelete={() => clearFilter(filter.key)}
                    color="primary"
                    variant="outlined"
                    className={classes.filterChip}
                  />
                ))}
              </Box>
            </div>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default FiltrosSimples;
